import { UserAnalyticsService } from "@analytics/index";

import {
  openAccountDoesNotExistNotification,
  openAccountExistsNotification,
  openContactSalesNotification,
  openEmailNotVerifiedNotification,
  openInvalidCredentialsNotification,
  openPasswordResetEmailFailedNotification,
  openPasswordResetEmailSuccessNotification,
  openPasswordResetFailedNotification,
  openPasswordResetSuccessNotification,
  openPrivacyPolicyAndTermsOfServiceConfirmation,
  openProductUpdateNotification,
  openUserUpdateFailedNotification,
  openUserUpdateSuccessAlert,
} from "@services/popups/user.popups";

import { OrganizationStore } from "@store/organizationStore";
import { ClearSmartSearchStore } from "@store/smartSearch";
import { UserActions } from "@store/userStore";

import * as Logger from "@utilities/Logger";

import ExternalApi from "../api/external.api";
import { getOrganization } from "../api/organization.api";
import UserApi from "../api/user.api";
import C from "../constants/constants";

import Http from "./http.service";

const SoftLaunch = false;

const checkShowProductUpdateModal = (user) => {
  if (SoftLaunch) return true;
  if (user.hasAcknowledgedProductUpdate) return true;
  openProductUpdateNotification(user);
  return false;
};

const checkPrivacyPolicyAcknowledgement = (user) => {
  if (user.isPrivacyPolicyAccepted && user.isTermsOfServiceAccepted) return true;
  openPrivacyPolicyAndTermsOfServiceConfirmation(user, checkShowProductUpdateModal(user));
  return false;
};

/**
 * Signs up a new user.
 *
 * @param {string} email - User's email address.
 * @param {string} password - User's password.
 * @param {string} firstName - User's first name.
 * @param {string} lastName - User's last name.
 * @param {function} navigate - Navigate function.
 */
const signUp = async (email, password, firstName, lastName, navigate) => {
  try {
    const onSafeList = await ExternalApi.checkSafeList(email);
    if (!onSafeList) {
      UserAnalyticsService.trackFailedSignup(firstName, lastName, email, "Not On Safelist");
      openContactSalesNotification();
      return;
    }
    const { message } = await ExternalApi.signUp(email, password, firstName, lastName);

    if (message === "Signup successful") {
      UserAnalyticsService.trackSignup(firstName, lastName, email, navigate);
      return;
    }

    openContactSalesNotification();
    // localStorage.setItem(C.AUTH_TOKEN_KEY, authToken);
    // localStorage.setItem(C.REDIRECT_KEY, "/");
    // await set(user);
    //
    // if (user.organizationId) {
    //   const userOrganization = await getOrganization(user.organizationId);
    //   user.organizationName = userOrganization.teamName;
    //   if (userOrganization) {
    //     setOrganization(userOrganization);
    //   }
    // } else {
    //   clearOrganization();
    // }
  } catch (error) {
    if (error.response?.data?.details?.includes("USER_EXISTS")) {
      UserAnalyticsService.trackFailedSignup(firstName, lastName, email, "User exists");
      openAccountExistsNotification();
    } else {
      UserAnalyticsService.trackFailedSignup(firstName, lastName, email, "Unknown");
      openContactSalesNotification();
    }
  }
};

const confirmEmail = async (email) => {
  try {
    await ExternalApi.confirmEmail(email);
  } catch (error) {
    if (error.response?.data?.details?.includes("EMAIL_NOT_VERIFIED")) {
      UserAnalyticsService.trackFailedConfirmEmail(email, "Email not verified");
    } else {
      UserAnalyticsService.trackFailedConfirmEmail(email, "Unknown");
    }
  }
};

/**
 * Signs in a user.
 *
 * @param {string} email - User email.
 * @param {string} password - User password.
 */
const signIn = async (email, password) => {
  try {
    const onSafeList = await ExternalApi.checkSafeList(email);
    if (!onSafeList) {
      UserAnalyticsService.trackFailedLogin(email, "Not On Safelist");
      openContactSalesNotification();
    }
    const { authToken, user } = await ExternalApi.signIn(email, password);
    localStorage.setItem(C.AUTH_TOKEN_KEY, authToken);
    if (user.organizationId) {
      const userOrganization = await getOrganization(user.organizationId);
      user.organizationName = userOrganization.teamName;
      if (userOrganization) {
        setOrganization(userOrganization);
      }
    } else {
      clearOrganization();
    }

    UserAnalyticsService.trackLogin();

    await set(user);
  } catch (error) {
    if (error.response?.data?.details?.includes("NO_USER_FOUND")) {
      UserAnalyticsService.trackFailedLogin(email, "User does not exist");
      openAccountDoesNotExistNotification();
    } else if (error.response?.data?.details?.includes("INCORRECT_PASSWORD")) {
      UserAnalyticsService.trackFailedLogin(email, "Incorrect password");
      openInvalidCredentialsNotification();
    } else if (error.response?.data?.details?.includes("EMAIL_NOT_VERIFIED")) {
      UserAnalyticsService.trackFailedLogin(email, "Email not verified");
      openEmailNotVerifiedNotification();
    } else {
      UserAnalyticsService.trackFailedLogin(email, "Unknown");
      openContactSalesNotification();
    }
    throw error;
  }
};

const clearUser = () => {
  ClearSmartSearchStore();
  UserActions.clearUser(); // clear Zustand
  localStorage.removeItem(C.USER_KEY); // clear localStore
  localStorage.removeItem(C.AUTH_TOKEN_KEY); // clear auth token
  localStorage.removeItem(C.SMART_SEARCH);
  Logger.log("user signed out");
};

const verifyEmail = (token) => {
  return ExternalApi.verifyEmail(token);
};

/**
 * Signs out a user, clearing user data in memory and redux store, also removes it from local storage.
 */
const signOut = async () => {
  clearUser();
  await UserApi.signOut();
};

/**
 * Sets Http user data.
 */
const setHttp = async () => {
  await Http.setHttp();
  await Http.setFileHttp();
};

const init = async (user) => {
  if (!localStorage.getItem(C.AUTH_TOKEN_KEY)) {
    Logger.warn("No user set when trying to set tokens");
    await signOut();
    return;
  }
  await setHttp();

  const hasAcknowledged = checkPrivacyPolicyAcknowledgement(user);
  if (hasAcknowledged) checkShowProductUpdateModal(user);
};

/**
 * Sets the user, initializing any services and saving it in local storage and redux store.
 *
 * @param {Object} user - The user data to set.
 */
const set = async (user) => {
  if (user) {
    UserActions.setUser({ ...user, authenticated: true }); // set in zustand
    await init(user);
  }
};

const setOrganization = async (organization) => {
  if (organization) {
    OrganizationStore.getState().actions.setOrganization(organization);
  }
};

const clearOrganization = async () => {
  OrganizationStore.getState().actions.clearOrganization();
};

/**
 * Checks if the user is authenticated.
 */
const isAuthenticated = async () => {
  return !!(await get());
};

/**
 * Gets the user data.
 */
const get = async () => {
  const authToken = localStorage.getItem(C.AUTH_TOKEN_KEY);

  if (authToken) {
    const { authToken: refreshedAuthToken, user: refreshedUser } = await UserApi.refresh(authToken);
    localStorage.setItem(C.AUTH_TOKEN_KEY, refreshedAuthToken);

    //SHOULD WE CHECK IF USER IS IN ORGANIZATION ON EACH USER DATA REFRESH?//

    if (refreshedUser.organizationId) {
      const userOrganization = await getOrganization(refreshedUser.organizationId);
      refreshedUser.organizationName = userOrganization.teamName;
      if (userOrganization) {
        setOrganization(userOrganization);
      }
    } else {
      clearOrganization();
    }
    await set(refreshedUser);
    UserAnalyticsService.trackRefresh();
    return refreshedUser;
  }
  return false;
};

/**
 * Sends a password reset email to the specified email address.
 *
 * @param {string} email - User's email address.
 */
const sendPasswordResetEmail = async (email) => {
  try {
    await ExternalApi.sendPasswordResetEmail(email);
    openPasswordResetEmailSuccessNotification();
  } catch {
    openPasswordResetEmailFailedNotification();
  }
};

/**
 * Resets the user's password.
 *
 * @param {string} password - New password.
 * @param {string} token - Reset token.
 */
const resetPassword = async (password, token) => {
  try {
    const { authToken, user } = await ExternalApi.resetPassword(password, token);
    localStorage.setItem(C.AUTH_TOKEN_KEY, authToken);
    localStorage.setItem(C.REDIRECT_KEY, "/");
    await set(user);
    openPasswordResetSuccessNotification();
  } catch {
    openPasswordResetFailedNotification();
  }
};

/**
 * Updates the user's data.
 *
 * @param {string} id - the id of the user to update
 * @param {Object} updateFields - The fields to update. It should contain an `_id` property to specify the user
 */
const update = async (id, updateFields) => {
  try {
    const updatedUser = await UserApi.update(id, updateFields);
    await set(updatedUser);
    openUserUpdateSuccessAlert();
  } catch {
    openUserUpdateFailedNotification();
  }
};
export default {
  checkPrivacyPolicyAcknowledgement,
  checkShowProductUpdateModal,
  clearUser,
  get,
  init,
  isAuthenticated,
  resetPassword,
  sendPasswordResetEmail,
  set,
  setHttp,
  signIn,
  signOut,
  signUp,
  update,
  verifyEmail,
};
